import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Button } from 'antd';
import { PlusOutlined, ReloadOutlined } from '@ant-design/icons';
import {
  RECURSO_EMPLEADOS_GESTIONAR, consultarEmpleados, leerCatalogosEmpleados, leerContadoresEmpleados,
} from '../api/empleadosApi';
import type { EmpleadoConsulta, EmpleadosContadores, EmpleadosCatalogos, EmpleadosPagina } from '../api/empleadosApi';
import { usePermissions } from '../contexts/PermissionContext';
import { ContadoresEmpleados } from '../components/Empleados/ContadoresEmpleados';
import { MetricasEmpleados } from '../components/Empleados/MetricasEmpleados';
import { FILTROS_INICIALES, FiltrosEmpleados, hayFiltrosActivos } from '../components/Empleados/FiltrosEmpleados';
import type { FiltrosListado } from '../components/Empleados/FiltrosEmpleados';
import { TablaEmpleados, VISTA_INICIAL } from '../components/Empleados/TablaEmpleados';
import type { VistaListado } from '../components/Empleados/TablaEmpleados';
import { FichaDrawer } from '../components/Empleados/FichaDrawer';
import { EmpleadoFormModal } from '../components/Empleados/EmpleadoFormModal';
import { errorEmpleado } from '../components/Empleados/errores';
import GcPagina from '../components/Layout/GcPagina';
import { FctTarjeta } from '../fct/react';

/** Cuerpo de `POST api/empleados/consulta` (§8): solo lo que tiene valor. */
const aConsulta = (f: FiltrosListado, v: VistaListado): EmpleadoConsulta => ({
  texto: f.texto || undefined,
  estado: f.estado,
  idDelegacion: f.idDelegacion,
  idZona: f.idZona,
  puesto: f.puesto,
  departamento: f.departamento,
  orden: v.orden,
  pagina: v.pagina,
  tamano: v.tamano,
});

const textoVacio = (f: FiltrosListado): string =>
  (hayFiltrosActivos(f) ? 'Ningún empleado cumple los filtros' : 'No hay empleados activos');

/**
 * Empleados (O-01, O-03): listado paginado en el servidor, con contadores, filtros y orden por columna, y ficha de
 * lectura, para Sistemas. La búsqueda va por POST (nada de nombres en la URL) y ni los filtros ni el texto se
 * guardan en la URL ni en el almacenamiento del navegador. Solo hay datos profesionales de Gescom, nada personal.
 * Recurso de la ruta: Empleados.Ver; las acciones de gestión (alta, edición, roles, baja) piden Empleados.Gestionar.
 */
const EmpleadosPage: React.FC = () => {
  const { hasPermission } = usePermissions();
  const gestionar = hasPermission(RECURSO_EMPLEADOS_GESTIONAR);

  const [filtros, setFiltros] = useState<FiltrosListado>(FILTROS_INICIALES);
  const [vista, setVista] = useState<VistaListado>(VISTA_INICIAL);
  const [pagina, setPagina] = useState<EmpleadosPagina | null>(null);
  const [cargando, setCargando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [contadores, setContadores] = useState<EmpleadosContadores | null>(null);
  const [errorContadores, setErrorContadores] = useState<string | null>(null);
  const [abierto, setAbierto] = useState<{ idEmpleado: number; nombre?: string } | null>(null);
  const [catalogos, setCatalogos] = useState<EmpleadosCatalogos | null>(null);
  const [errorCatalogos, setErrorCatalogos] = useState<string | null>(null);
  const [nuevoAbierto, setNuevoAbierto] = useState(false);

  // Solo vale la respuesta de la última petición: una lenta no puede pisar a una más reciente.
  const ultima = useRef(0);
  const ultimaContadores = useRef(0);

  const cargar = useCallback(async () => {
    const n = ++ultima.current;
    setCargando(true);
    setError(null);
    try {
      const r = await consultarEmpleados(aConsulta(filtros, vista));
      if (n !== ultima.current) return;
      // Tras una baja o un filtro más estrecho, la página pedida puede haberse quedado fuera: se va a la última.
      if (r.elementos.length === 0 && r.total > 0 && vista.pagina > 1) {
        setVista((v) => ({ ...v, pagina: Math.max(1, Math.ceil(r.total / v.tamano)) }));
        return;
      }
      setPagina(r);
    } catch (e) {
      // Con error no se conserva la página anterior: los filtros siguen puestos y el reintento los reutiliza.
      if (n === ultima.current) {
        setPagina(null);
        setError(errorEmpleado(e).mensaje);
      }
    } finally {
      if (n === ultima.current) setCargando(false);
    }
  }, [filtros, vista]);

  useEffect(() => { cargar(); }, [cargar]);

  const cargarContadores = useCallback(async () => {
    const n = ++ultimaContadores.current;
    setErrorContadores(null);
    try {
      const r = await leerContadoresEmpleados();
      if (n === ultimaContadores.current) setContadores(r);
    } catch (e) {
      if (n === ultimaContadores.current) {
        setContadores(null);
        setErrorContadores(errorEmpleado(e).mensaje);
      }
    }
  }, []);

  useEffect(() => { cargarContadores(); }, [cargarContadores]);

  // Tras un alta, una edición o una baja cambian la fila y también los números de arriba.
  const trasCambio = useCallback(() => { cargar(); cargarContadores(); }, [cargar, cargarContadores]);

  // Los catálogos (delegaciones, zonas, puestos, departamentos y roles) alimentan los filtros y los formularios.
  const cargarCatalogos = useCallback(() => {
    setErrorCatalogos(null);
    leerCatalogosEmpleados().then(setCatalogos).catch((e) => setErrorCatalogos(errorEmpleado(e).mensaje));
  }, []);
  useEffect(() => { cargarCatalogos(); }, [cargarCatalogos]);

  // Un cambio de filtro vuelve a la página 1.
  const cambiarFiltros = (cambio: Partial<FiltrosListado>) => {
    setFiltros((f) => ({ ...f, ...cambio }));
    setVista((v) => ({ ...v, pagina: 1 }));
  };
  const limpiarFiltros = () => {
    setFiltros(FILTROS_INICIALES);
    setVista((v) => ({ ...v, pagina: 1 }));
  };

  return (
    <GcPagina seccion="Empleados" titulo="Empleados" contexto="Sistemas · Fichas de empleados" densidad="compacta"
      acciones={(
        <>
          <MetricasEmpleados contadores={contadores} error={errorContadores} onReintentar={cargarContadores} />
          {gestionar && (
            <Button type="primary" icon={<PlusOutlined />} disabled={!catalogos} onClick={() => setNuevoAbierto(true)}>
              Nuevo empleado
            </Button>
          )}
        </>
      )}
    >
      <FctTarjeta>
        <ContadoresEmpleados
          contadores={contadores}
          estado={filtros.estado}
          onEstado={(estado) => cambiarFiltros({ estado })}
        />

        <FiltrosEmpleados filtros={filtros} catalogos={catalogos} onCambio={cambiarFiltros} onLimpiar={limpiarFiltros} />

        {errorCatalogos && (
          <Alert
            type="warning"
            showIcon
            message={`No se han podido cargar las listas de los filtros y los formularios. ${errorCatalogos}`}
            action={<Button size="small" icon={<ReloadOutlined />} onClick={cargarCatalogos}>Reintentar</Button>}
            style={{ marginBottom: 12 }}
          />
        )}

        <TablaEmpleados
          filas={pagina?.elementos ?? []}
          total={pagina?.total ?? 0}
          vista={vista}
          cargando={cargando}
          error={error}
          textoVacio={textoVacio(filtros)}
          onVista={setVista}
          onAbrir={(r) => setAbierto({ idEmpleado: r.idEmpleado, nombre: r.nombreCompleto })}
          onReintentar={cargar}
        />
      </FctTarjeta>

      <FichaDrawer
        idEmpleado={abierto?.idEmpleado ?? null}
        nombre={abierto?.nombre}
        gestionar={gestionar}
        catalogos={catalogos}
        onCambiado={trasCambio}
        onCerrar={() => setAbierto(null)}
      />
      {gestionar && (
        <EmpleadoFormModal
          abierto={nuevoAbierto}
          catalogos={catalogos}
          onCerrar={() => setNuevoAbierto(false)}
          onCambiado={trasCambio}
          onAbrirFicha={(idEmpleado) => setAbierto({ idEmpleado })}
        />
      )}
    </GcPagina>
  );
};

export default EmpleadosPage;
